import { memo, useEffect, useMemo, useState } from "react";
import Button from "../../staff/Button";
import ImagePreviewUpload from "../ImagePreviewUpload/ImagePreviewUpload";
import DropDownMultiple from "../../staff/DropDownMultiple";
import InputText from "../../staff/InputText";
import { PhoneNumber } from "../../staff/PhoneNumber";
import {
  Honorary,
  MemberType,
  MemberTypeString,
  Suffix,
} from "../../../Enums/PersonEnums";
import { useSelector } from "react-redux";
import { Appstore } from "../../../redux";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import { getFirmById } from "../../../Services/Firm";
import { getOfficeByFirm } from "../../../Services/Office";
import { usePersonForm } from "./personForm/PersonFormContext";
import ToggleSwitch from "../../staff/ToogleSwitch";
import { getFirms } from "../../../Services/Firm";
import IFirm from "../../../interfaces/models/IFirm";
import RadioGroup from "../../staff/RadioGroup";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { sendResetInvitation } from "../../../Services/EmailSend";
import { toastError, toastPromise } from "../../../utils/toast";
import { formatOfficeText } from "../../../utils/functions";
import { fileToJpegDataUrl } from "../../../utils/fileToJpegDataUrl";
import {
  normalizeListingCategories,
  resolveFirmTitleOptions,
} from "../../../utils/firmListingCategory";
import { firmAudienceName } from "../../../utils/firmDisplayName";

interface ProfileProps {
  offices?: IOfficeNew[];
  catalogMode?: boolean;
  /** When set (e.g. member site without Manage Firm Redux), drives Title / Listing Category options. */
  listingCategoryOverride?: string[];
  /** When set (e.g. Manage Firm route), drives Title options from that firm's id. */
  firmIdForTitleOptions?: string;
  /** Default true; set false on member self-edit profile page. */
  showResetPassword?: boolean;
  /** When true, Main Office shows as required for members regardless of isAttorney. */
  mainOfficeRequired?: boolean;
  /** Override outer shell classes (e.g. flush layout inside a parent card). */
  shellClassName?: string;
}

const ProfileComponent: React.FC<ProfileProps> = ({
  offices: officesProp,
  catalogMode = false,
  listingCategoryOverride,
  firmIdForTitleOptions,
  showResetPassword = true,
  mainOfficeRequired = false,
  shellClassName,
}) => {
  const {
    form,
    setField,
    setFormPatch,
    touchField,
    getFieldError,
    shouldShowError,
    editPersonId,
    editPersonUpdatedAt,
  } = usePersonForm();

  const professionalFirm = useSelector(
    (state: Appstore) => state.manageFirm,
  ).selectedFirm;

  const [firmOffices, setFirmOffices] = useState<IOfficeNew[]>(
    officesProp ?? [],
  );
  const [firmOptions, setFirmOptions] = useState<IFirm[]>([]);
  const [resolvedListingCategory, setResolvedListingCategory] = useState<
    string[] | undefined
  >();
  const { isStaffMode } = useStaffMode();

  const firmIdForTitles =
    firmIdForTitleOptions?.trim() ||
    form.firm?.trim() ||
    professionalFirm?.id?.trim() ||
    "";

  const honoraryOptions = useMemo(
    () => Object.values(Honorary).map((h: string) => ({ id: h, name: h })),
    [],
  );
  const suffixOptions = useMemo(
    () => Object.values(Suffix).map((s: string) => ({ id: s, name: s })),
    [],
  );

  useEffect(() => {
    if (listingCategoryOverride !== undefined) {
      setResolvedListingCategory(
        normalizeListingCategories(listingCategoryOverride),
      );
      return;
    }

    if (!firmIdForTitles) {
      setResolvedListingCategory(undefined);
      return;
    }

    let cancelled = false;
    void getFirmById(firmIdForTitles)
      .then((firm) => {
        if (cancelled) {
          return;
        }
        setResolvedListingCategory(
          normalizeListingCategories(firm?.listingCategory),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedListingCategory(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [firmIdForTitles, listingCategoryOverride]);

  const titleOptions = useMemo(() => {
    const titles = resolveFirmTitleOptions(resolvedListingCategory);
    return titles.map((title: string) => ({ id: title, name: title }));
  }, [resolvedListingCategory]);

  const memberTypeRadioOptions = useMemo(
    () =>
      [MemberType.member, MemberType.nonMember, MemberType.staff].map((mt) => ({
        id: String(mt),
        name: MemberTypeString[mt as keyof typeof MemberTypeString],
      })),
    [],
  );

  useEffect(() => {
    if (catalogMode) {
      getFirms().then((firms) => {
        setFirmOptions(firms);
      });
    }
  }, [catalogMode, professionalFirm]);

  useEffect(() => {
    if (officesProp?.length) {
      setFirmOffices(officesProp);
      return;
    }
    const firmId = form.firm || professionalFirm?.id;
    if (!firmId) {
      return;
    }
    getOfficeByFirm(firmId).then(setFirmOffices);
  }, [officesProp, form.firm, professionalFirm?.id]);

  const selectedSuffix = useMemo(() => {
    if (!form.suffix) {
      return null;
    }
    return (
      suffixOptions.find((o) => o.id === form.suffix) ?? {
        id: form.suffix,
        name: form.suffix,
      }
    );
  }, [form.suffix, suffixOptions]);

  const selectedTitle = useMemo(() => {
    if (!form.title) {
      return null;
    }
    return (
      titleOptions.find((o) => o.id === form.title) ?? {
        id: form.title,
        name: form.title,
      }
    );
  }, [form.title, titleOptions]);

  const selectedMainOffice = useMemo(() => {
    if (!form.mainOffice) {
      return null;
    }
    return firmOffices.find((x) => x.id === form.mainOffice);
  }, [form.mainOffice, firmOffices]);

  const searchableLocked = (form.areasOfPractice ?? []).length === 0;
  const searchableChecked = Boolean(form.isSearchable) && !searchableLocked;
  const searchableHint = searchableLocked
    ? "Add an area of practice to make this profile searchable"
    : undefined;

  const shell =
    shellClassName ??
    "flex flex-col items-start gap-5 bg-white w-full p-4 lg:p-6";

  const handleResetPassword = async () => {
    const userId = form.userId?.trim();
    if (!userId) {
      toastError("This person does not have a login account yet.");
      return;
    }

    await toastPromise(sendResetInvitation(userId), {
      pending: "Sending password reset invitation...",
      success: "Password reset invitation sent.",
    });
  };

  return (
    <div className={shell}>
      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-row items-center gap-5 flex-wrap w-full">
          <h3
            className={`font-decimal text-xl font-semibold md:text-2xl flex-1 ${isStaffMode ? "text-secondary" : "text-primary"}`}
          >
            Profile
          </h3>
          <div className="hidden lg:flex">
            <ToggleSwitch
              onChange={(checked) => setField("isAttorney", checked)}
              checked={Boolean(form.isAttorney)}
              label="Practicing Attorney"
            />
            <ToggleSwitch
              onChange={(checked) => setField("isSearchable", checked)}
              checked={searchableChecked}
              disabled={searchableLocked}
              tooltip={searchableHint}
              label="Searchable within Firm Listing"
            />
          </div>
          {showResetPassword && form.userId?.trim() ? (
            <div className="flex justify-end">
              <Button
                text="Reset Password"
                onClick={handleResetPassword}
                color={isStaffMode ? "secondary" : "primary"}
                variant="filled"
                size="fixed"
              />
            </div>
          ) : null}
          <div className="lg:hidden flex flex-col md:flex-row justify-between gap-5">
            <ToggleSwitch
              onChange={(checked) => setField("isAttorney", checked)}
              checked={Boolean(form.isAttorney)}
              label="Practicing Attorney"
            />
            <ToggleSwitch
              onChange={(checked) => setField("isSearchable", checked)}
              checked={searchableChecked}
              disabled={searchableLocked}
              tooltip={searchableHint}
              label="Searchable within Firm Listing"
            />
          </div>
        </div>
      </div>
      {searchableHint ? (
        <p className="-mt-3 w-full text-right text-xs text-gray-500">
          {searchableHint}
        </p>
      ) : null}

      <div className="flex w-full flex-col gap-5 md:flex-row md:flex-wrap md:items-start lg:flex-nowrap">
        <div className="md:max-w-[155px] lg:max-w-none">
          <ImagePreviewUpload
            title="Upload Profile Image"
            personId={editPersonId}
            photoUpdatedAt={editPersonUpdatedAt}
            photo={form.photo}
            setImage={(value) => {
              if (value instanceof File) {
                fileToJpegDataUrl(value)
                  .then((dataUrl) => {
                    setField("photo", dataUrl);
                    touchField("photo");
                  })
                  .catch(() => {
                    toastError("Could not process the image. Try JPG or PNG.");
                  });
                return;
              }
              if (typeof value === "string") {
                setField("photo", value);
                touchField("photo");
              } else if (value === null) {
                setField("photo", "");
                touchField("photo");
              }
            }}
            maxsize={25}
            extension="JPG, PNG"
            removable
            hideBorder
            modalClassName="max-w-[500px]"
          />
        </div>
        <div className="flex w-full min-w-0 flex-1 flex-col gap-5 md:contents lg:flex lg:flex-1 lg:min-w-0 lg:flex-col lg:gap-5">
          <div className="grid w-full min-w-0 grid-cols-1 gap-5 md:min-w-0 md:flex-1 md:grid-cols-2 lg:flex-none lg:grid-cols-4 2xl:grid-cols-5">
            {catalogMode && (
              <div className="col-span-1 grid grid-cols-1 gap-5 sm:grid-cols-2 md:col-span-2 lg:col-span-3">
                <RadioGroup
                  title="Member Type"
                  required
                  orientation="horizontal"
                  options={memberTypeRadioOptions}
                  value={String(form.memberType)}
                  onChange={(v) => {
                    setField("memberType", Number(v) as MemberType);
                    touchField("memberType");
                  }}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                />
                <DropDownMultiple
                  title="Firm"
                  options={firmOptions.map((firm) => ({
                    id: firm.id,
                    name: firmAudienceName(firm, isStaffMode),
                  }))}
                  value={form.firm}
                  onChange={(opt) => {
                    setField("firm", opt?.id ?? "");
                    touchField("firm");
                  }}
                />
              </div>
            )}
            <DropDownMultiple
              title="Honorary Title"
              options={honoraryOptions}
              value={
                honoraryOptions.find((o) => o.id === form.honorary) ?? null
              }
              onChange={(opt) => {
                setField("honorary", opt?.name ?? "");
                touchField("honorary");
              }}
            />
            <InputText
              title="First Name"
              value={form.name}
              onChange={(v) => setField("name", v)}
              onBlur={() => touchField("name")}
              required
              error={shouldShowError("name") && Boolean(getFieldError("name"))}
              errorMessage={getFieldError("name")}
            />
            <InputText
              title="Middle Initial"
              value={form.middleInitial ?? ""}
              onChange={(v) => setField("middleInitial", v)}
              onBlur={() => touchField("middleInitial")}
              error={
                shouldShowError("middleInitial") &&
                Boolean(getFieldError("middleInitial"))
              }
              errorMessage={getFieldError("middleInitial")}
            />
            <InputText
              title="Last Name"
              value={form.lastName}
              onChange={(v) => setField("lastName", v)}
              onBlur={() => touchField("lastName")}
              required
              error={
                shouldShowError("lastName") &&
                Boolean(getFieldError("lastName"))
              }
              errorMessage={getFieldError("lastName")}
            />
            <DropDownMultiple
              title="Suffix"
              options={suffixOptions}
              value={selectedSuffix}
              onChange={(opt) => {
                setField("suffix", opt?.name ?? "");
                touchField("suffix");
              }}
            />
            <InputText
              title="Firm Email"
              value={form.firmEmail ?? ""}
              onChange={(v) => setField("firmEmail", v)}
              onBlur={() => touchField("firmEmail")}
              autoComplete="new-email"
              error={
                shouldShowError("firmEmail") &&
                Boolean(getFieldError("firmEmail"))
              }
              errorMessage={getFieldError("firmEmail")}
              required
            />
          </div>

          <div className="grid w-full min-w-0 grid-cols-1 gap-5 md:basis-full md:grow-0 md:shrink-0 md:grid-cols-2 lg:basis-auto lg:grid-cols-4 2xl:grid-cols-5">
            <div className="col-span-1 flex flex-col justify-start gap-2 md:col-span-2">
              <ToggleSwitch
                onChange={(checked) => setField("unlistedTitleToggle", checked)}
                checked={form.unlistedTitleToggle}
                label="Unlisted Title"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={
                    form.unlistedTitleToggle ? "md:order-2" : "md:order-1"
                  }
                >
                  <DropDownMultiple
                    title={
                      form.unlistedTitleToggle ? "Listing Category" : "Title"
                    }
                    options={titleOptions}
                    value={selectedTitle}
                    onChange={(opt) => {
                      setField("title", opt?.name ?? "");
                      touchField("title");
                    }}
                    error={
                      shouldShowError("title") &&
                      Boolean(getFieldError("title"))
                    }
                    errorMessage={getFieldError("title")}
                    required
                  />
                </div>
                <div
                  className={
                    form.unlistedTitleToggle ? "md:order-1" : "md:order-2"
                  }
                >
                  <InputText
                    title="Unlisted Title"
                    value={form.unlistedTitle ?? ""}
                    onChange={(v) => setField("unlistedTitle", v)}
                    onBlur={() => touchField("unlistedTitle")}
                    error={
                      shouldShowError("unlistedTitle") &&
                      Boolean(getFieldError("unlistedTitle"))
                    }
                    errorMessage={getFieldError("unlistedTitle")}
                    disabled={!form.unlistedTitleToggle}
                    required={form.unlistedTitleToggle}
                  />
                </div>
              </div>
            </div>

            <div className="col-span-1 flex flex-col justify-start gap-2 md:col-span-2">
              <ToggleSwitch
                onChange={(checked) => setField("hasAssistant", checked)}
                checked={Boolean(form.hasAssistant)}
                label="CC Assistant on WSG Communications"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputText
                  title="Assistant Name"
                  value={form.assistantName ?? ""}
                  onChange={(v) => setField("assistantName", v)}
                  onBlur={() => touchField("assistantName")}
                  error={
                    shouldShowError("assistantName") &&
                    Boolean(getFieldError("assistantName"))
                  }
                  errorMessage={getFieldError("assistantName")}
                  disabled={!form.hasAssistant}
                  required={form.hasAssistant}
                />
                <InputText
                  title="Assistant Email"
                  value={form.assistantEmail ?? ""}
                  onChange={(v) => setField("assistantEmail", v)}
                  onBlur={() => touchField("assistantEmail")}
                  error={
                    shouldShowError("assistantEmail") &&
                    Boolean(getFieldError("assistantEmail"))
                  }
                  errorMessage={getFieldError("assistantEmail")}
                  disabled={!form.hasAssistant}
                  required={form.hasAssistant}
                />
              </div>
            </div>
          </div>

          <div className="grid w-full min-w-0 grid-cols-1 gap-5 md:basis-full md:grow-0 md:shrink-0 md:grid-cols-2 lg:basis-auto lg:grid-cols-4 2xl:grid-cols-5">
            <DropDownMultiple
              title="Main Office"
              required={
                mainOfficeRequired ||
                (form.memberType === MemberType.member && form.isAttorney)
              }
              options={firmOffices.map((office: IOfficeNew) => ({
                id: office.id ?? "",
                name: formatOfficeText(office),
              }))}
              value={selectedMainOffice?.id ?? ""}
              onChange={(opt) => {
                setField("mainOffice", opt?.id != null ? String(opt.id) : "");
                touchField("mainOffice");
              }}
              error={
                shouldShowError("mainOffice") &&
                Boolean(getFieldError("mainOffice"))
              }
              errorMessage={getFieldError("mainOffice")}
            />
            <PhoneNumber
              title="Office Phone Number"
              seter={() => {}}
              Obj={
                selectedMainOffice?.phoneNumber ?? {
                  code: "",
                  phone: "",
                  basecode: "",
                }
              }
              error={Boolean(
                shouldShowError("phoneNumber") && getFieldError("phoneNumber"),
              )}
              errormsj={getFieldError("phoneNumber")}
              disabled
            />
            <PhoneNumber
              title="Direct Phone Number"
              Obj={form.phoneNumber ?? { code: "", phone: "", basecode: "" }}
              seter={(v) => {
                setFormPatch({ phoneNumber: v });
                touchField("phoneNumber");
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Profile = memo(ProfileComponent);
export default Profile;
